import {create} from 'zustand';

import type {Challenge, Question, Answer, SubmitAnswerResponse} from './types';
import type {ChallengeConfig} from '../play/types';

type Actions = {
  onChallengeJoined: (params: {
    game: ChallengeConfig;
    challenge: Challenge;
    attemptId: string;
    firstQuestion: Question;
  }) => void;

  // meant to be used when receiving socket events
  onChallengeClosed: () => void;
  onPrizeAttached: (prizeId: number) => void;

  onSubmitAnswerSuccess: (result: SubmitAnswerResponse, answers: Answer[]) => void;
  onOutOfTime: () => void;
  viewScore: () => void;
  startChallenge: () => void;
  reset: () => void;
};

type State =
  | {
      state: '';
      challenge: null;
      currentQuestion: null;
      currentAnswer: [];
      attemptId: '';
      currentScore: 0;
      answeredQuestions: 0;
      points: 0;
      wrongAnswer: [];
      isClosed: false;
      prizeId: null;
      language: null;
      prevAppLanguage: null;
      hasAttachedGuestPrize: null;
      game: null;
    }
  | {
      state: 'warm-up' | 'question' | 'out-of-time' | 'result';
      challenge: Challenge;
      currentQuestion: Question;
      currentAnswer: Answer[];
      attemptId: string;
      wrongAnswer: Answer[];
      isClosed: boolean;
      prizeId: number;
      currentScore: number;
      answeredQuestions: number;
      points: number;
      language: Language;
      prevAppLanguage: Language;
      hasAttachedGuestPrize: boolean;
      game: ChallengeConfig;
    };

type TriviaStore = State & Actions;

const initialState: State = {
  state: '',
  attemptId: '',
  wrongAnswer: [],
  isClosed: false,
  currentScore: 0,
  answeredQuestions: 0,
  points: 0,
  prizeId: null,
  challenge: null,
  currentQuestion: null,
  currentAnswer: [],
  language: null,
  prevAppLanguage: null,
  hasAttachedGuestPrize: null,
  game: null,
};

export const useTriviaStore = create<TriviaStore>((set, get) => ({
  ...initialState,
  onChallengeJoined: (params) => {
    set({
      state: 'warm-up',
      attemptId: params.attemptId,
      challenge: params.challenge,
      currentQuestion: params.firstQuestion,
      game: params.game,
    });
  },
  onChallengeClosed: () => set({isClosed: true}),
  onPrizeAttached: (prizeId) => set({prizeId}),
  onSubmitAnswerSuccess: (result, answers) => {
    switch (result.state) {
      case 'go_to_next_question':
        set({
          state: 'question',
          currentScore: result.current_score,
          answeredQuestions: result.answered_questions,
          points: result.points,
          currentQuestion: result.next_question,
          currentAnswer: [],
        });
        break;
      case 'loser':
        const challenge = get().challenge;

        if (challenge?.termination_mode === 'stop_on_wrong_answer' && !answers.length) {
          return set({state: 'out-of-time'});
        }

        set({
          state: 'result',
          wrongAnswer: answers,
          currentScore: result.current_score,
          answeredQuestions: result.answered_questions,
          points: result.points,
        });
        break;
      case 'winner':
        set({
          state: 'result',
          currentScore: result.current_score,
          answeredQuestions: result.answered_questions,
          points: result.points,
        });
        break;
    }
  },
  onOutOfTime: () => set({state: 'out-of-time'}),
  viewScore: () => set({state: 'result'}),
  startChallenge: () => set({state: 'question'}),
  reset: () => set(initialState),
}));
